import { Badge, Button, Divider, Skeleton, theme, Tooltip } from "antd";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { BellFilled, InboxOutlined } from "@ant-design/icons";
import API from "API";
import dayjs from "dayjs";
import { debounce } from "lodash";
import { useNavigate } from "react-router-dom";
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/vi';

dayjs.extend(relativeTime);
dayjs.locale("vi");

const { useToken } = theme;
const PAGESIZE = 5;

function NotificationList({ notifications, setNotificaitons, unRead, setNotReadCount,
    setUnRead, getting, stopFetching, setPage }) {
    const navigate = useNavigate();
    const handleClick = useCallback((notification) => {
        if (!notification.read) {
            setNotReadCount(value => value - 1);
            API.post("/student/make-notification-read/", {notification_id: notification.id});
            notification.read = true;
            const index = notifications.findIndex(item => item.id === notification.id);
            notifications[index] = {...notification};
            setNotificaitons([...notifications]);
        }
        if (notification.link) {
            navigate(notification.link);
        }
    },[notifications, navigate, setNotificaitons, setNotReadCount])
  
    return <div className="style-scrollbar" style={{ width: "min(calc(100vw - 30px), 370px)" }}>
        <div style={{ padding: "7px 3px" }}>
            <div className={"noti-tabs" + (!unRead ? " active" : "")} onClick={() => {
                if (getting) return;
                setUnRead(false);
                setPage(1);
            }}>Tất cả</div>
            |
            <div className={"noti-tabs" + (unRead ? " active" : "")} onClick={() => {
                if (getting) return;
                setUnRead(true);
                setPage(1);
            }}>Chưa đọc</div>
        </div>
        {notifications.map(item => <div key={item.id} onClick={() => handleClick(item)} className={`notification-div${item.read ? "" : " un-read"}`}>
            <div dangerouslySetInnerHTML={{ __html: item.content}}/>
            <Tooltip title={item.created_at.format("DD/MM/YYYY HH:mm:ss")}>
                <i style={{ color: "rgb(191, 191, 191)", fontSize: 12, fontWeight: "bold" }}>
                    {item.created_at.fromNow()}
                </i>
            </Tooltip>
            <Divider style={{ marginTop: 3, marginBottom: 0, borderColor: "rgb(191, 191, 191)"}}/>
        </div>)}
        {!stopFetching && <Skeleton active paragraph={{ rows: 2 }} title={false}/>}
        {stopFetching && notifications.length === 0 && <div className="notification-div" style={{ textAlign: "center", paddingTop: 15, paddingBottom: 15 }}>
            <InboxOutlined style={{ fontSize: 30 }}/><br />
            Bạn không có bất kỳ thông báo nào
        </div>}
    </div>
}

export default function NotificationButton() {
    const { token } = useToken();
    const [openNoti, setOpenNoti] = useState(false);
    const [notReadCount, setNotReadCount] = useState(0);
    const [notifications, setNotificaitons] = useState([]);
    const [stopFetching, setStopFetching] = useState(false);
    const [page, setPage] = useState(1);
    const [getting, setGetting] = useState(true);
    const [unRead, setUnRead] = useState(false);
    const toolTipDiv = useRef();
    const lastUnRead = useRef(false);
    const insideToolTipDiv = useRef();
    const getNotificationsAPI = useCallback((newList=false, unRead, page) => {
        setGetting(true);
        if (newList) {
            setNotificaitons([]);
        }
        API.get("/student/get-notifications/", {params: {page_size: PAGESIZE, un_read: unRead, page: page}})
        .then(res => {
            for (const r of res.data.notifications) {
                r.created_at = dayjs(r.created_at);
            }
            setNotificaitons(notifications => [...notifications, ...res.data.notifications]);
            setNotReadCount(res.data.count_not_read);
            if (res.data.notifications.length === PAGESIZE) {
                setTimeout(() => {
                    const container = insideToolTipDiv.current;
                    if (+container.clientHeight === 0) return;
                    if (container.scrollTop + container.clientHeight >= container.scrollHeight - 50) {
                        setPage(page => page+1);
                    }
                },100)
            } else {
                setStopFetching(true);
            }
        })
        .catch(error => {
            console.log(error);
        })
        .finally(() => {
            setGetting(false);
        })
    },[])

    const getNotificationsDebounce = useMemo(() => 
        debounce((...args) => {
            getNotificationsAPI(...args);
        }, 500)
    ,[getNotificationsAPI])

    const getNotifications = useCallback((...args) => {
        setGetting(true);
        getNotificationsDebounce(...args);
    },[getNotificationsDebounce])

    useEffect(() => {
        if (insideToolTipDiv.current) {
            insideToolTipDiv.current.onscroll = () => {
                const container = insideToolTipDiv.current;
                if (container.scrollTop + container.clientHeight >= container.scrollHeight - 50) {
                    if (!getting && !stopFetching) {
                        setPage(page => page+1);
                        setGetting(true);
                    }
                }
            }
            return;
        }
        const interval = setInterval(() => {    
            insideToolTipDiv.current = toolTipDiv.current?.querySelector(".ant-tooltip");
            if (insideToolTipDiv.current) {
                insideToolTipDiv.current.onscroll = () => {
                    const container = insideToolTipDiv.current;
                    if (container.scrollTop + container.clientHeight >= container.scrollHeight - 50) {
                        if (!getting && !stopFetching) {
                            setPage(page => page+1);
                            setGetting(true);
                        }
                    }
                }
                clearInterval(interval);
            }
        },100)
        return () => clearInterval(interval)
    },[getting, stopFetching])

    useEffect(() => {
        if (lastUnRead.current !== unRead) {
            getNotificationsAPI(true, unRead, page);
        } else {
            getNotifications(false, unRead, page);
        }
        lastUnRead.current = unRead;
    },[getNotifications, getNotificationsAPI, unRead, page])

    const handleOpen = useCallback((open) => {
        if (!open) return;
        setTimeout(() => {
            const container = insideToolTipDiv.current;
            if (container.scrollTop + container.clientHeight >= container.scrollHeight - 50) {
                if (!getting && !stopFetching) {
                    setPage(page => page+1);
                }
            }
        },100)
    },[getting, stopFetching])

    return <>
        <div ref={toolTipDiv} className="notification-popup"/>
        <Tooltip open={openNoti || undefined} color="rgb(56, 56, 56)" className="noti-tooltip"
            getPopupContainer={() => toolTipDiv.current} placement={"topRight"}
            title={<NotificationList notifications={notifications}
            setNotificaitons={setNotificaitons} setPage={setPage}
            unRead={unRead} setUnRead={setUnRead} getting={getting}
            stopFetching={stopFetching} setStopFetching={setStopFetching}
            setNotReadCount={setNotReadCount}
            />}
            forceRender
            onOpenChange={handleOpen}
        >
            <Button
                className="me-1"
                shape="circle"
                style={{
                    backgroundColor: openNoti ? "white" : "rgba(0,0,0,0)",
                    borderWith: 2,
                    borderColor: openNoti ? token.colorPrimary : "white",
                }}
                onClick={() => setOpenNoti(!openNoti)}
            >
                <Badge count={notReadCount} offset={[-20, -7]}>
                    <BellFilled style={{
                        color: openNoti ? token.colorPrimary : "white",
                    }}/>
                </Badge>
            </Button>
        </Tooltip>
    </>;
}
